import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FeatureType } from '../../../generated/ApiClient';
import { EnhancedDialog } from '../../../component/EnhancedDialog';
import { FormTextField } from '../../../component/form/FormTextField';
import { FeatureValueInput } from '../components/FeatureValueInput';

interface FeatureClassification {
  id: string;
  name: string;
  featureType: FeatureType;
  value?: string;
}

interface FeatureSubmissionData {
  id: string;
  name: string;
  featureType: FeatureType;
  value: any;
}

export function ObjectFeatureDialog({
  isOpen,
  onSubmit,
  onClose,
  feature,
}: {
  isOpen: boolean;
  onSubmit: (data: FeatureSubmissionData) => void;
  onClose: () => void;
  feature: FeatureClassification | null;
}) {
  const { t } = useTranslation();
  const isEditMode = !!feature;

  const schema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    featureType: z.nativeEnum(FeatureType),
    value: z.any(),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: '',
      name: '',
      featureType: FeatureType.Text,
      value: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (feature) {
        reset({
          id: feature.id,
          name: feature.name,
          featureType: feature.featureType,
          value: feature.value || '',
        });
      } else {
        reset({
          id: '',
          name: '',
          featureType: FeatureType.Text,
          value: '',
        });
      }
    }
  }, [isOpen, feature, reset]);

  const onFormSubmit = (data: FormValues) => {
    onSubmit({
      id: data.id,
      name: data.name,
      featureType: data.featureType,
      value: data.value,
    });
  };

  return (
    <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={onClose}>
      <DialogTitle>
        {isEditMode
          ? t('classification.features.editFeature')
          : t('classification.features.addFeature')}
      </DialogTitle>
      <DialogContent dividers>
        <form id="feature-value-form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <Grid container spacing={3} sx={{ paddingY: 2 }}>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="id"
                label={t('classification.features.id')}
                disabled={isSubmitting || isEditMode}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="name"
                label={t('classification.features.name')}
                disabled={isSubmitting || isEditMode}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FeatureValueInput
                control={control}
                label={t('classification.features.value')}
                typeFieldName="featureType"
                valueFieldName="value"
                disabled={isSubmitting}
                readOnly={isEditMode}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={onClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="feature-value-form"
            variant="contained"
            disabled={isSubmitting}
          >
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
}
